import React, {
  useCallback, useMemo, useRef, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dropdown, Menu, Input,
} from 'antd';
import {
  GroupOutlined,
  FormOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';

import {
  CUSTOM_EXPORT_PROP_TYPE,
} from '../../settings/Exports/exports.constants';

import { includesTerm } from '../../helpers/helpers';

function ExportSelector({
  exportList,
  selectedExport,
  onExportSelected,
}) {
  const { paidFlags = [] } = useSelector((state) => state.settings.company) || {};
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);

  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState();
  const [open, setOpen] = useState(false);

  const onClick = useCallback(({ key: exportId }) => {
    onExportSelected(exportId);
  }, [onExportSelected]);

  const filteredExports = useMemo(() => {
    const exports = exportList.filter((ex) => selectedDivisions.has(ex.divisionId));
    if (paidFlags.includes('AB_WORK_REPORTING')) {
      exports.push({
        id: 'abWorkReporting',
        title: 'AB Work Reporting',
        type: 'abWorkReporting',
      });
    }



    return exports.filter((ex) => (
      ex.title
      && includesTerm(ex.title, searchTerm)
    ));
  }, [exportList, selectedDivisions, searchTerm, paidFlags]);

  const onSearchChange = useCallback((e) => {
    const {
      target: {
        value: searchText,
      } = {},
    } = e;
    setSearchTerm(searchText);
  }, []);

  // Auto focus search upon opening
  useEffect(() => {
    if (open && inputRef && inputRef.current) {
      // Don't like this, but couldn't get it to work without timeout
      // We do similar in PDFDesignerItem
      setTimeout(inputRef.current.focus, 200);
    }
  }, [open, inputRef]);

  return (
    <span>
      <Dropdown
        trigger={['click']}
        onVisibleChange={setOpen}
        overlay={(
          <Menu style={{ maxHeight: 500, overflow: 'scroll', paddingTop: 0 }}>
            <Menu.Item
              disabled
              style={{
                cursor: 'default',
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                borderBottom: '1px lightgray solid',
                zIndex: 1000,
                paddingTop: 4,
              }}
            >
              <Input.Search
                key="Search"
                type="search"
                className="searchbar"
                placeholder="Search"
                allowClear
                onChange={onSearchChange}
                ref={inputRef}
              />
            </Menu.Item>
            {filteredExports.map((customExport) => {
              const { id: exportId, type } = customExport;
              const style = {};
              if (selectedExport && exportId === selectedExport.id) style.fontWeight = 'bold';

              // Add icon based on export type
              let icon = <FormOutlined />;
              if (type === 'timeEntries') {
                icon = <FieldTimeOutlined />;
              } else if (type === 'boardCards') {
                icon = <GroupOutlined />;
              }

              return (
                <Menu.Item
                  key={exportId}
                  style={style}
                  onClick={onClick}
                >
                  <span style={{ marginRight: 8 }}>{icon}</span>
                  {customExport.title}
                </Menu.Item>
              );
            })}
          </Menu>
      )}
      >
        <span id="board-crumb-button">
          {selectedExport ? selectedExport.title : 'Custom Export'}
        </span>
      </Dropdown>
    </span>
  );
}

ExportSelector.propTypes = {
  exportList: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedExport: CUSTOM_EXPORT_PROP_TYPE,
  onExportSelected: PropTypes.func.isRequired,
};

ExportSelector.defaultProps = {
  selectedExport: null,
};

export default ExportSelector;
